import { Box, Center } from '@chakra-ui/react';
import { StickerSetType, useAppStore } from '@halo/chat-sdk-react';
import { DotLottieReact, type Data, type DotLottie } from '@lottiefiles/dotlottie-react';
import { useMemoizedFn } from 'ahooks';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { safeJsonParse } from '@halo/utils';
import { Panel, type Tab } from './Panel';
import EmojiIcon from './icons/emoji.svg?react';
import StickerIcon from './icons/sticker.svg?react';
import { formatEmojiId } from './utils';

const tabs: Array<Tab> = [
  { icon: <EmojiIcon />, id: StickerSetType.Emoji, cols: 11, miniCols: 8 },
  /* { icon: <GifIcon />, id: StickerSetType.Custom }, */
  { icon: <StickerIcon />, id: StickerSetType.Sticker, cols: 5, miniCols: 5 },
];

// Wrapper component that plays animation on mouse hover
function HoverSticker({ data }: { data: Data }) {
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

  useEffect(() => {
    dotLottie?.setFrame(1);
  }, [dotLottie]);

  return (
    <Box w="full" h="full">
      <DotLottieReact
        onMouseEnter={() => dotLottie?.play()}
        onMouseLeave={() => dotLottie?.pause()}
        data={typeof data === 'string' ? safeJsonParse(data, data) : data}
        dotLottieRefCallback={setDotLottie}
        useFrameInterpolation={true}
      />
    </Box>
  );
}

function renderStringSticker(str: Data, type: StickerSetType) {
  if (type === StickerSetType.Sticker) {
    return <HoverSticker data={str} />;
  }
  return <Fragment>{formatEmojiId(str as string)}</Fragment>;
}

export interface EmojiProps {
  defaultTab?: StickerSetType;
  mini?: boolean;
  onSelect: (emojiId: string, groupId: string, type: StickerSetType) => void;
}

function Emoji({ defaultTab = StickerSetType.Emoji, mini = false, onSelect }: EmojiProps) {
  const [tab, setTab] = useState<StickerSetType>(defaultTab);
  // Icon state extracted to external, because different tabs have different display formats and data fetching
  const [icons, setIcons] = useState<Record<string, React.ReactNode>>({});

  const client = useAppStore((state) => state.client);

  // Batch update icons: collect multiple updates and update once to avoid performance issues from frequent updates
  const pendingUpdatesRef = useRef<Record<string, React.ReactNode>>({});
  const rafRef = useRef<number | null>(null);

  const handleIconUpdate = useMemoizedFn((updates: Record<string, React.ReactNode>) => {
    // Collect updates
    pendingUpdatesRef.current = { ...updates, ...pendingUpdatesRef.current };

    // If there's already a pending batch update, no need to schedule again
    if (rafRef.current !== null) {
      return;
    }

    // Batch update: use requestAnimationFrame to batch update in the next frame
    rafRef.current = requestAnimationFrame(() => {
      const updatesToApply = { ...pendingUpdatesRef.current };
      pendingUpdatesRef.current = {};
      setIcons((prev) => ({ ...updatesToApply, ...prev }));
      rafRef.current = null;
    });
  });

  const handleRequestGroups = useMemoizedFn(async (tab: StickerSetType) => {
    if (!client) return [];
    const data = await client.Api.emoji.getStickerSets(tab);

    return data.map(({ title, set_id, logo }) => ({
      id: set_id,
      name: title,
      logo: renderStringSticker(logo, tab),
    }));
  });

  const handleRequestGroupItems = useMemoizedFn(async (groupId: string) => {
    if (!client) return [];
    const res = await client.Api.emoji.getStickerDetail({
      set_ids: [groupId],
    });
    return (res[groupId] || []).map(({ id }) => ({ id, name: '' }));
  });

  const handleRequestGroupItemIcon = useMemoizedFn(async (itemId: string) => {
    let str: Data | undefined = undefined;
    // todo Alan: get emoji data from emoji_id
    if (tab === StickerSetType.Emoji) {
      str = itemId;
    }
    if (tab === StickerSetType.Sticker) {
      const res = await client?.Api.emoji.getStickerData({ sticker_id: itemId });
      if (res?.render_data) str = res.render_data;
    }
    if (!str) return;
    // Directly update icon, unified update method
    handleIconUpdate({ [itemId]: renderStringSticker(str, tab) });
  });

  // Clear icons when tab changes, because different tabs have different data
  const handleTabChange = useMemoizedFn((newTab: StickerSetType) => {
    setTab(newTab);
    setIcons({});
  });

  const handleRenderItem = useMemoizedFn(
    ({ icon, type }: { id: string; icon: React.ReactNode; type: StickerSetType }) => {
      if (type === StickerSetType.Sticker)
        return (
          <Box w="full" h="full" p="1">
            {icon}
          </Box>
        );
      return (
        <Center w="full" h="full" fontSize="3xl">
          {icon}
        </Center>
      );
    }
  );

  return (
    <Panel
      mini={mini}
      tabs={tabs}
      tab={tab}
      onTabChange={handleTabChange}
      requestGroups={handleRequestGroups}
      requestGroupItems={handleRequestGroupItems}
      requestGroupItemIcon={handleRequestGroupItemIcon}
      icons={icons}
      renderItem={handleRenderItem}
      onSelect={onSelect}
    />
  );
}

function MiniPanel(props: Omit<EmojiProps, 'mini'>) {
  return <Emoji {...props} mini={true} />;
}

Emoji.MiniPanel = MiniPanel;

export { StickerTypeIcon } from './StickerTypeIcon';

// eslint-disable-next-line react-refresh/only-export-components
export { Emoji, formatEmojiId };
